import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { errMsg, useApi } from "@/lib/api";
import EdgeToggle from "./EdgeToggle";
import DomainPanel from "@/components/domains/DomainPanel";
import SeoSettings from "@/components/quality/SeoSettings";

export default function SiteSettings({ site, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ name: site.name, slug: site.slug, site_url: site.site_url || "", auto_preview: !!site.auto_preview, demo_mode: !!site.demo_mode }); }, [site]);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/sites/${site.id}`, f); toast.success("Impostazioni salvate"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const dirty = f.name !== site.name || f.slug !== site.slug || f.site_url !== (site.site_url || "") || f.auto_preview !== !!site.auto_preview || f.demo_mode !== !!site.demo_mode;
  return (
    <div className="space-y-8">
    <div className="grid max-w-2xl gap-4" data-testid="site-settings">
      <EdgeToggle site={site} onSaved={onSaved} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label className="text-xs">Nome del sito</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="site-settings-name" /></div>
        <div><Label className="text-xs">Identificativo</Label><Input className="mt-1" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })} data-testid="site-settings-slug" />
          <p className="mt-1 text-[11px] text-slate-500">Cambia gli indirizzi «anteprima» e «online» del sito.</p></div>
      </div>
      <div><Label className="text-xs">Indirizzo definitivo</Label><Input className="mt-1" value={f.site_url} placeholder="https://www.cliente.it" onChange={(e) => setF({ ...f, site_url: e.target.value })} data-testid="site-settings-url" />
        <p className="mt-1 text-[11px] text-slate-500">Usato per sitemap e canonical dalla prossima compilazione. Si imposta da solo quando il dominio personalizzato (qui sotto) diventa attivo.</p></div>
      <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
        <Switch checked={f.auto_preview} onCheckedChange={(v) => setF({ ...f, auto_preview: v })} data-testid="site-settings-auto-preview" />
        <span className="text-sm text-slate-700">Aggiorna l'anteprima a ogni salvataggio di versione<span className="block text-xs text-slate-500">Salvando una versione nell'editor parte una compilazione automatica; la pubblicazione online resta manuale.</span></span>
      </label>
      <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
        <Switch checked={f.demo_mode} onCheckedChange={(v) => setF({ ...f, demo_mode: v })} data-testid="site-settings-demo-mode" />
        <span className="text-sm text-slate-700">Sito dimostrativo (solo per presentazioni commerciali)<span className="block text-xs text-slate-500">Se attivo, moduli simulati (form, carrelli, prenotazioni finte) possono andare online come avviso. Se spento, sono errori bloccanti: un sito reale pubblica solo moduli collegati.</span></span>
      </label>
      <div className="text-xs text-slate-500">Cliente: <b className="text-slate-700">{site.client.name}</b> · Progetto: <b className="text-slate-700">{site.project.name}</b> (fissi: per spostare un sito crea un nuovo sito e archivia questo)</div>
      <div><Button onClick={save} disabled={!dirty || busy} className="bg-sky-600 hover:bg-sky-700" data-testid="site-settings-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Salva impostazioni</Button></div>
    </div>
    <SeoSettings site={site} onSaved={onSaved} />
    <DomainPanel site={site} onChanged={onSaved} />
    </div>
  );
}
